import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import AdminSidebar from "../../sidebar/admin_sidebar";
import AdminHeader from "../../header/admin_header";
import { apiCall } from "../../../_services/apiCall";
import config from "../../../config/config.json";
import Loader from "../../loader/Loader";
import NoDataImg from "../../../img/no-results.png";

// ── Palette ───────────────────────────────────────────────────────────────────
const SCOPES = [
  { code: "SCOPE_1", label: "Scope 1", sub: "Direct Emissions",  color: "#e05c3a", light: "#fff5f2", border: "#f5c9bc", icon: "🔥" },
  { code: "SCOPE_2", label: "Scope 2", sub: "Indirect — Energy", color: "#2b7fe0", light: "#f0f6ff", border: "#bcd6f7", icon: "⚡" },
  { code: "SCOPE_3", label: "Scope 3", sub: "Value Chain",       color: "#1baa6f", light: "#f0faf5", border: "#a5dfc3", icon: "🌿" },
];

// ── Toggle ────────────────────────────────────────────────────────────────────
function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      style={{
        width: 38, height: 22, borderRadius: 11,
        background: checked ? "#1baa6f" : "#d5dde8",
        border: "none", cursor: "pointer", position: "relative",
        transition: "background 0.25s", flexShrink: 0, outline: "none",
      }}
    >
      <span style={{
        position: "absolute", top: 3, left: checked ? 19 : 3,
        width: 16, height: 16, borderRadius: "50%", background: "#fff",
        boxShadow: "0 1px 4px rgba(0,0,0,0.22)",
        transition: "left 0.22s cubic-bezier(.4,0,.2,1)",
      }} />
    </button>
  );
}

// ── Slide-in Drawer ───────────────────────────────────────────────────────────
function DrawerPanel({ visible, onClose, title, subtitle, children }) {
  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.38)",
        backdropFilter: "blur(2px)", zIndex: 999,
        opacity: visible ? 1 : 0, pointerEvents: visible ? "auto" : "none",
        transition: "opacity 0.28s",
      }} />
      <div style={{
        position: "fixed", top: 0, right: 0, height: "100%",
        width: "clamp(320px, 38vw, 480px)", background: "#fff",
        boxShadow: "-8px 0 40px rgba(0,0,0,0.14)", zIndex: 1000,
        transform: visible ? "translateX(0)" : "translateX(100%)",
        transition: "transform 0.32s cubic-bezier(.4,0,.2,1)",
        display: "flex", flexDirection: "column", overflowY: "auto",
      }}>
        <div style={{
          padding: "24px 28px 20px", borderBottom: "1px solid #eef1f5",
          display: "flex", alignItems: "flex-start", justifyContent: "space-between",
          background: "#fafbfc",
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a", fontFamily: "'Sora',sans-serif", letterSpacing: "-0.3px" }}>
              {title}
            </div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>{subtitle}</div>
          </div>
          <button onClick={onClose} style={{
            width: 30, height: 30, borderRadius: 8, border: "1px solid #e2e8f0",
            background: "#fff", cursor: "pointer", display: "flex",
            alignItems: "center", justifyContent: "center",
            fontSize: 16, color: "#64748b", flexShrink: 0,
          }}>×</button>
        </div>
        <div style={{ flex: 1, padding: "24px 28px" }}>{children}</div>
      </div>
    </>
  );
}

// ── Per-scope Inline Add Row ──────────────────────────────────────────────────
function InlineAddRow({ scopeGroup, onSaved, showToast }) {
  const [open,      setOpen]      = useState(false);
  const [value,     setValue]     = useState("");
  const [isActive,  setIsActive]  = useState(true);   // ← status toggle for quick-add
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 80);
  }, [open]);

  const reset = () => { setOpen(false); setValue(""); setIsActive(true); setError(""); };

  const handleSave = async () => {
    if (!value.trim())           { setError("Name is required.");  return; }
    if (value.trim().length < 3) { setError("Min 3 characters.");  return; }
    setSaving(true);
    const { isSuccess } = await apiCall(
      `${config.ADMIN_API_URL}createEmissionCategory`, {},
      { name: value.trim(), scope: scopeGroup.code, is_active: isActive },
      "POST"
    );
    setSaving(false);
    if (isSuccess) {
      showToast(`"${value.trim()}" added to ${scopeGroup.label}.`);
      reset();
      onSaved();
    } else {
      showToast("Failed to save. Try again.", "error");
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter")  handleSave();
    if (e.key === "Escape") reset();
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          width: "100%", padding: "10px 18px",
          background: "transparent", border: "none",
          borderTop: `1px dashed ${scopeGroup.border}`,
          cursor: "pointer", color: scopeGroup.color,
          fontSize: 12, fontWeight: 700, fontFamily: "'Sora', sans-serif",
          transition: "background 0.15s",
        }}
        onMouseEnter={e => e.currentTarget.style.background = scopeGroup.light}
        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
      >
        <span style={{
          width: 20, height: 20, borderRadius: "50%",
          background: scopeGroup.color, color: "#fff",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          fontSize: 15, lineHeight: 1, flexShrink: 0,
        }}>+</span>
        Add category to {scopeGroup.label}
      </button>
    );
  }

  return (
    <div style={{
      padding: "14px 18px",
      borderTop: `1.5px solid ${scopeGroup.border}`,
      background: scopeGroup.light,
      animation: "fadeSlideDown 0.18s ease",
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: scopeGroup.color,
        fontFamily: "'Sora',sans-serif", marginBottom: 10,
        letterSpacing: "0.04em", textTransform: "uppercase",
      }}>
        {scopeGroup.icon} New category — {scopeGroup.label}
      </div>

      {/* Name input + action buttons */}
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={e => { setValue(e.target.value); if (error) setError(""); }}
            onKeyDown={handleKey}
            placeholder="e.g. Stationary Combustion"
            style={{
              width: "100%", padding: "9px 12px", borderRadius: 8,
              border: `1.5px solid ${error ? "#e05c3a" : scopeGroup.border}`,
              fontSize: 13, color: "#0f172a", background: "#fff", outline: "none",
              fontFamily: "'DM Sans', sans-serif", boxSizing: "border-box",
              boxShadow: error
                ? "0 0 0 3px rgba(224,92,58,0.12)"
                : `0 0 0 3px ${scopeGroup.color}18`,
            }}
          />
          {error && (
            <div style={{ fontSize: 11, color: "#e05c3a", marginTop: 4, fontWeight: 600 }}>
              ⚠ {error}
            </div>
          )}
          <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 5 }}>
            Press{" "}
            <kbd style={{ background:"#e8ecf0", borderRadius:3, padding:"1px 4px", fontSize:10 }}>Enter</kbd>
            {" "}to save &nbsp;·&nbsp;{" "}
            <kbd style={{ background:"#e8ecf0", borderRadius:3, padding:"1px 4px", fontSize:10 }}>Esc</kbd>
            {" "}to cancel
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: "9px 16px", background: scopeGroup.color,
            color: "#fff", border: "none", borderRadius: 8,
            fontSize: 12, fontWeight: 700,
            cursor: saving ? "not-allowed" : "pointer",
            fontFamily: "'Sora', sans-serif", opacity: saving ? 0.7 : 1,
            flexShrink: 0, display: "flex", alignItems: "center", gap: 6,
            whiteSpace: "nowrap",
          }}
        >
          {saving
            ? <span style={{ width:12, height:12, border:"2px solid #fff", borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.7s linear infinite", display:"inline-block" }} />
            : "Save"
          }
        </button>
        <button onClick={reset} style={{
          padding: "9px 12px", background: "#fff", color: "#64748b",
          border: "1.5px solid #dde4ed", borderRadius: 8,
          fontSize: 12, fontWeight: 700, cursor: "pointer",
          fontFamily: "'Sora', sans-serif", flexShrink: 0,
        }}>
          Cancel
        </button>
      </div>

      {/* ── Active status toggle (inline add) ── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginTop: 12, padding: "9px 12px",
        background: "#fff", borderRadius: 8,
        border: `1.5px solid ${scopeGroup.border}`,
      }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#334155", fontFamily: "'Sora',sans-serif" }}>
            Active Status
          </div>
          <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 1 }}>
            {isActive ? "Visible to users" : "Hidden from users"}
          </div>
        </div>
        <Toggle checked={isActive} onChange={setIsActive} />
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function ScopeCategoryMaster() {
  const location = useLocation();

  const [loading,     setLoading]     = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [drawerOpen,  setDrawerOpen]  = useState(false);
  const [scopesData,  setScopesData]  = useState([]);
  const [expandedIds, setExpandedIds] = useState({ SCOPE_1: true });
  const [errors,      setErrors]      = useState({});
  const [toast,       setToast]       = useState(null);
  const nameRef = useRef(null);

  const EMPTY_FORM = { id: null, name: "", scope: "SCOPE_1", is_active: true };
  const [formData, setFormData] = useState(EMPTY_FORM);
  const isEditing = !!formData.id;

  useEffect(() => { fetchEmissionCategories(); }, []);

  useEffect(() => {
    if (drawerOpen) setTimeout(() => nameRef.current?.focus(), 340);
  }, [drawerOpen]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  };

  // ────────────────────────────────────────────────────────────────────────────
  // FIX: controller returns { success: true, data: [...] }
  //      so list lives at data.data (apiCall wraps response as `data`)
  // ────────────────────────────────────────────────────────────────────────────
  const fetchEmissionCategories = async () => {
    setLoading(true);
    const { isSuccess, data } = await apiCall(
      `${config.ADMIN_API_URL}getEmissionCategory`, {}, {}, "GET"
    );
    if (isSuccess) {
      const list = Array.isArray(data?.data) ? data.data : [];
      const grouped = SCOPES.map(s => ({
        ...s,
        categories: list.filter(item => item.scope === s.code),
      }));
      setScopesData(grouped);
    }
    setLoading(false);
  };

  const openAdd = (scopeCode) => {
    setFormData({ ...EMPTY_FORM, scope: scopeCode || "SCOPE_1" });
    setErrors({});
    setDrawerOpen(true);
  };

  const openEdit = (cat) => {
    setFormData({ id: cat.id, name: cat.name, scope: cat.scope, is_active: cat.is_active });
    setErrors({});
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setFormData(EMPTY_FORM);
    setErrors({});
  };

  const validate = () => {
    const e = {};
    if (!formData.name.trim())                e.name = "Category name is required.";
    else if (formData.name.trim().length < 3) e.name = "Must be at least 3 characters.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSaving(true);

    
    const url = isEditing
      ? `${config.ADMIN_API_URL}updateEmissionCategory`
      : `${config.ADMIN_API_URL}createEmissionCategory`;

    const payload = isEditing
      ? { id: formData.id, name: formData.name, scope: formData.scope, is_active: formData.is_active }
      : { name: formData.name, scope: formData.scope, is_active: formData.is_active };

    const { isSuccess } = await apiCall(url, {}, payload, "POST");

    setSaving(false);
    if (isSuccess) {
      showToast(isEditing ? "Category updated." : "Category added.");
      closeDrawer();
      fetchEmissionCategories();
    } else {
      showToast("Something went wrong. Try again.", "error");
    }
  };

  // ────────────────────────────────────────────────────────────────────────────
  // FIX: only send { id, is_active } — don't spread the whole item
  //      (avoids sending stale / unneeded fields and matches controller signature)
  // ────────────────────────────────────────────────────────────────────────────
  const toggleStatus = async (item) => {
    const newActive = !item.is_active;
    const { isSuccess } = await apiCall(
      `${config.ADMIN_API_URL}updateEmissionCategory`, {},
      { id: item.id, is_active: newActive },
      "POST"
    );
    if (isSuccess) {
      showToast(`"${item.name}" ${newActive ? "activated" : "deactivated"}.`);
      fetchEmissionCategories();
    } else {
      showToast("Failed to update status.", "error");
    }
  };

  const hasAnyCategories = scopesData.some(s => s.categories.length > 0);
  const totalAll    = scopesData.reduce((sum, s) => sum + s.categories.length, 0);
  const totalActive = scopesData.reduce((sum, s) => sum + s.categories.filter(c => c.is_active).length, 0);

  const inputStyle = (hasError) => ({
    width: "100%", padding: "10px 13px", borderRadius: 8,
    border: `1.5px solid ${hasError ? "#e05c3a" : "#dde4ed"}`,
    fontSize: 13, color: "#0f172a", background: "#fff", outline: "none",
    transition: "border-color 0.18s", fontFamily: "'DM Sans', sans-serif",
    boxSizing: "border-box",
  });

  const labelStyle = {
    display: "block", fontSize: 12, fontWeight: 700, color: "#334155",
    marginBottom: 6, letterSpacing: "0.03em", textTransform: "uppercase",
    fontFamily: "'Sora', sans-serif",
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&family=DM+Mono:wght@400;500&display=swap');
        .cat-row:hover                  { background: #f8fafc !important; }
        .cat-row:hover .edit-btn        { opacity: 1 !important; }
        .scope-header:hover             { background: #f8fafc !important; }
        .add-btn:hover                  { background: #1467c7 !important; transform: translateY(-1px); box-shadow: 0 4px 14px rgba(43,127,224,0.3) !important; }
        .add-btn                        { transition: all 0.18s !important; }
        .save-btn:hover:not(:disabled)  { background: #1467c7 !important; transform: translateY(-1px); }
        .save-btn                       { transition: all 0.18s; }
        .save-btn:disabled              { opacity: 0.65; cursor: not-allowed; }
        input:focus, select:focus       { border-color: #2b7fe0 !important; box-shadow: 0 0 0 3px rgba(43,127,224,0.12) !important; }
        @keyframes toastIn       { from { opacity:0; transform:translateY(12px) scale(0.97); } to { opacity:1; transform:none; } }
        @keyframes fadeSlideDown { from { opacity:0; transform:translateY(-6px); }             to { opacity:1; transform:none; } }
        @keyframes spin          { to   { transform:rotate(360deg); } }
      `}</style>

      <div>
        <AdminSidebar dataFromParent={location.pathname} />
        <AdminHeader />

        {/* Toast */}
        {toast && (
          <div style={{
            position: "fixed", bottom: 28, right: 28, zIndex: 2000,
            background: toast.type === "error" ? "#e05c3a" : "#1baa6f",
            color: "#fff", borderRadius: 10, padding: "12px 20px",
            fontSize: 13, fontWeight: 600, boxShadow: "0 6px 24px rgba(0,0,0,0.18)",
            animation: "toastIn 0.25s ease", display: "flex",
            alignItems: "center", gap: 10, fontFamily: "'DM Sans', sans-serif", maxWidth: 340,
          }}>
            <span style={{ fontSize: 16 }}>{toast.type === "error" ? "⚠️" : "✓"}</span>
            {toast.msg}
          </div>
        )}

        <div className="main_wrapper">
          <div className="inner_wraapper">
            <div className="hol_rell">
              <div className="steps-form">
                <div className="steps-row setup-panel">

                  {/* Tab nav */}
                  <div className="tabs-top setting_admin my-0">
                    <ul>
                      <li><NavLink to="/settings">My Profile</NavLink></li>
                      <li><NavLink to="/settings/billing">Billing</NavLink></li>
                      <li><NavLink to="/settings/access_management">Access</NavLink></li>
                      <li><NavLink to="/settings/emission-category" className="activee">Emission Category</NavLink></li>
                      <li><NavLink to="/settings/financial_year">Financial Year</NavLink></li>
                      <li><NavLink to="/settings/sub-users">Sub Users</NavLink></li>
                      <li><NavLink to="/settings/category">Category</NavLink></li>
                      <li><NavLink to="/settings/unit">Unit</NavLink></li>
                    </ul>
                  </div>

                  {/* Global add button */}
                  <button
                    className="add-btn"
                    onClick={() => openAdd()}
                    style={{
                      display: "flex", alignItems: "center", gap: 7,
                      background: "#2b7fe0", color: "#fff", border: "none",
                      borderRadius: 9, padding: "9px 18px", fontSize: 13,
                      fontWeight: 700, cursor: "pointer",
                      fontFamily: "'Sora', sans-serif", whiteSpace: "nowrap",
                    }}
                  >
                    <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
                    Add Category
                  </button>
                </div>
              </div>
            </div>

            {/* ── Content ── */}
            <div className="color_div_on framwork_2 pt-0">
              {loading ? <Loader /> : !hasAnyCategories ? (

                /* Empty state */
                <div style={{
                  textAlign: "center", padding: "60px 24px",
                  background: "#fff", borderRadius: 14,
                  border: "1.5px dashed #dde4ed",
                }}>
                  <img src={NoDataImg} alt="No Data" style={{ width: 160, opacity: 0.55, marginBottom: 20 }} />
                  <div style={{ fontSize: 17, fontWeight: 800, color: "#0f172a", fontFamily: "'Sora',sans-serif", marginBottom: 6 }}>
                    No Categories Yet
                  </div>
                  <div style={{ fontSize: 13, color: "#64748b", marginBottom: 24 }}>
                    Start by adding emission categories for Scope 1, 2 &amp; 3.
                  </div>
                  <button onClick={() => openAdd()} style={{
                    background: "#2b7fe0", color: "#fff", border: "none",
                    borderRadius: 9, padding: "10px 22px", fontSize: 13,
                    fontWeight: 700, cursor: "pointer", fontFamily: "'Sora', sans-serif",
                  }}>
                    + Add First Category
                  </button>
                </div>

              ) : (

                /* ── Main list ── */
                <div style={{
                  fontFamily: "'DM Sans', sans-serif",
                  background: "#f4f7fb", borderRadius: 14, padding: 20,
                }}>

                  {/* Header bar */}
                  <div style={{
                    background: "#fff", borderRadius: "10px 10px 0 0",
                    padding: "14px 22px", display: "flex",
                    alignItems: "center", justifyContent: "space-between",
                    borderBottom: "1px solid #eef1f5", marginBottom: 14,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 20 }}>🌍</span>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a", fontFamily: "'Sora',sans-serif" }}>
                          Scope &amp; Category Master
                        </div>
                        <div style={{ fontSize: 11, color: "#94a3b8" }}>Master Data Management</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      {[
                        { label: "Total",  value: totalAll,    color: "#64748b" },
                        { label: "Active", value: totalActive, color: "#1baa6f" },
                      ].map(stat => (
                        <div key={stat.label} style={{
                          textAlign: "center", background: "#f8fafc",
                          border: "1px solid #eef1f5", borderRadius: 8, padding: "5px 14px",
                        }}>
                          <div style={{ fontSize: 18, fontWeight: 800, color: stat.color, fontFamily: "'Sora',sans-serif", lineHeight: 1.2 }}>
                            {stat.value}
                          </div>
                          <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, letterSpacing: "0.04em" }}>
                            {stat.label.toUpperCase()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Scope accordion cards */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {scopesData.map(scopeGroup => {
                      const isExpanded  = !!expandedIds[scopeGroup.code];
                      const activeCount = scopeGroup.categories.filter(c => c.is_active).length;

                      return (
                        <div
                          key={scopeGroup.code}
                          style={{
                            background: "#fff",
                            border: `1.5px solid ${isExpanded ? scopeGroup.border : "#eef1f5"}`,
                            borderRadius: 12, overflow: "hidden",
                            boxShadow: isExpanded ? `0 2px 12px ${scopeGroup.color}18` : "none",
                            transition: "border-color 0.2s, box-shadow 0.2s",
                          }}
                        >
                          {/* Scope header */}
                          <div
                            className="scope-header"
                            onClick={() => setExpandedIds(p => ({ ...p, [scopeGroup.code]: !isExpanded }))}
                            style={{
                              display: "flex", alignItems: "center",
                              padding: "14px 18px", cursor: "pointer", userSelect: "none",
                              borderLeft: `4px solid ${scopeGroup.color}`,
                              transition: "background 0.15s",
                            }}
                          >
                            <span style={{ fontSize: 18, marginRight: 10 }}>{scopeGroup.icon}</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                <span style={{ fontSize: 13, fontWeight: 800, color: "#0f172a", fontFamily: "'Sora',sans-serif" }}>
                                  {scopeGroup.label}
                                </span>
                                <span style={{
                                  fontSize: 10, fontWeight: 700, color: scopeGroup.color,
                                  background: scopeGroup.light, border: `1px solid ${scopeGroup.border}`,
                                  borderRadius: 5, padding: "1px 7px", fontFamily: "'DM Mono',monospace",
                                }}>
                                  {scopeGroup.code.replace("_", " ")}
                                </span>
                                <span style={{ fontSize: 11, color: "#94a3b8" }}>— {scopeGroup.sub}</span>
                              </div>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <span style={{
                                fontSize: 11, fontWeight: 700, color: scopeGroup.color,
                                background: scopeGroup.light, borderRadius: 20,
                                padding: "2px 10px", fontFamily: "'DM Mono',monospace",
                              }}>
                                {activeCount}/{scopeGroup.categories.length}
                              </span>
                              <span style={{
                                fontSize: 10, color: "#94a3b8",
                                transform: isExpanded ? "rotate(180deg)" : "rotate(0)",
                                transition: "transform 0.22s", display: "inline-block",
                              }}>▼</span>
                            </div>
                          </div>

                          {/* Expanded body */}
                          {isExpanded && (
                            <div style={{
                              borderTop: `1.5px solid ${scopeGroup.border}`,
                              background: scopeGroup.light,
                              animation: "fadeSlideDown 0.2s ease",
                            }}>

                              {/* Empty notice */}
                              {scopeGroup.categories.length === 0 && (
                                <div style={{
                                  fontSize: 12, color: "#94a3b8",
                                  textAlign: "center", padding: "18px 18px 4px",
                                }}>
                                  No categories for {scopeGroup.label} yet — add one below.
                                </div>
                              )}

                              {/* Category rows */}
                              {scopeGroup.categories.map((cat, idx) => (
                                <div
                                  key={cat.id}
                                  className="cat-row"
                                  style={{
                                    display: "flex", alignItems: "center",
                                    justifyContent: "space-between", padding: "11px 18px",
                                    borderBottom: idx < scopeGroup.categories.length - 1
                                      ? `1px solid ${scopeGroup.border}` : "none",
                                    transition: "background 0.15s", background: "transparent",
                                  }}
                                >
                                  {/* Left: dot + name */}
                                  <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
                                    <div style={{
                                      width: 7, height: 7, borderRadius: "50%",
                                      background: cat.is_active ? scopeGroup.color : "#c8d3de",
                                      flexShrink: 0, transition: "background 0.2s",
                                    }} />
                                    <span style={{
                                      fontSize: 13, fontWeight: 500,
                                      color: cat.is_active ? "#0f172a" : "#94a3b8",
                                      overflow: "hidden", textOverflow: "ellipsis",
                                      whiteSpace: "nowrap", transition: "color 0.2s",
                                    }}>
                                      {cat.name}
                                    </span>
                                    {!cat.is_active && (
                                      <span style={{
                                        fontSize: 9, fontWeight: 700, color: "#94a3b8",
                                        background: "#f1f5f9", borderRadius: 4,
                                        padding: "1px 5px", letterSpacing: "0.05em", flexShrink: 0,
                                      }}>
                                        INACTIVE
                                      </span>
                                    )}
                                  </div>

                                  {/* Right: edit + toggle */}
                                  <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
                                    <button
                                      className="edit-btn"
                                      onClick={() => openEdit(cat)}
                                      style={{
                                        opacity: 0, background: "#fff",
                                        border: `1px solid ${scopeGroup.border}`,
                                        borderRadius: 7, padding: "4px 12px",
                                        fontSize: 11, fontWeight: 700,
                                        color: scopeGroup.color, cursor: "pointer",
                                        transition: "opacity 0.18s",
                                        fontFamily: "'Sora', sans-serif", whiteSpace: "nowrap",
                                      }}
                                    >
                                      ✏ Edit
                                    </button>
                                    <Toggle checked={!!cat.is_active} onChange={() => toggleStatus(cat)} />
                                  </div>
                                </div>
                              ))}

                              {/* ── Per-scope inline quick-add ── */}
                              <InlineAddRow
                                scopeGroup={scopeGroup}
                                onSaved={fetchEmissionCategories}
                                showToast={showToast}
                              />

                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Edit / full-add drawer ── */}
        <DrawerPanel
          visible={drawerOpen}
          onClose={closeDrawer}
          title={isEditing ? "Edit Category" : "Add New Category"}
          subtitle={isEditing ? "Update the details below" : "Fill in the details to create a new emission category"}
        >
          <form onSubmit={handleSubmit} noValidate>

            {/* Name */}
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Category Name</label>
              <input
                ref={nameRef}
                type="text"
                value={formData.name}
                onChange={e => {
                  setFormData({ ...formData, name: e.target.value });
                  if (errors.name) setErrors({ ...errors, name: null });
                }}
                placeholder="e.g. Stationary Combustion"
                style={inputStyle(errors.name)}
              />
              {errors.name && (
                <div style={{ fontSize: 11, color: "#e05c3a", marginTop: 5, fontWeight: 600 }}>
                  ⚠ {errors.name}
                </div>
              )}
            </div>

            {/* Scope radio cards */}
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Select Scope</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {SCOPES.map(s => {
                  const selected = formData.scope === s.code;
                  return (
                    <label key={s.code} style={{
                      display: "flex", alignItems: "center", gap: 12,
                      padding: "11px 14px", borderRadius: 9,
                      border: `1.5px solid ${selected ? s.color : "#dde4ed"}`,
                      background: selected ? s.light : "#fafbfc",
                      cursor: "pointer", transition: "all 0.18s",
                    }}>
                      <input
                        type="radio" name="scope" value={s.code}
                        checked={selected}
                        onChange={() => setFormData({ ...formData, scope: s.code })}
                        style={{ display: "none" }}
                      />
                      <span style={{
                        width: 16, height: 16, borderRadius: "50%",
                        border: `2px solid ${selected ? s.color : "#c0cbd8"}`,
                        background: selected ? s.color : "transparent",
                        flexShrink: 0, display: "flex", alignItems: "center",
                        justifyContent: "center", transition: "all 0.18s",
                      }}>
                        {selected && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff" }} />}
                      </span>
                      <span style={{ fontSize: 14 }}>{s.icon}</span>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: selected ? s.color : "#334155", fontFamily: "'Sora',sans-serif" }}>
                          {s.label}
                        </div>
                        <div style={{ fontSize: 11, color: "#64748b" }}>{s.sub}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Active toggle */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "13px 15px", background: "#f8fafc",
              borderRadius: 9, border: "1.5px solid #eef1f5", marginBottom: 28,
            }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#334155", fontFamily: "'Sora',sans-serif" }}>
                  Active Status
                </div>
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                  {formData.is_active ? "Category is active and visible" : "Category is hidden from users"}
                </div>
              </div>
              <Toggle checked={!!formData.is_active} onChange={v => setFormData({ ...formData, is_active: v })} />
            </div>

            {/* Buttons */}
            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="submit"
                disabled={saving}
                className="save-btn"
                style={{
                  flex: 1, padding: "11px 0", background: "#2b7fe0",
                  color: "#fff", border: "none", borderRadius: 9,
                  fontSize: 13, fontWeight: 700, cursor: "pointer",
                  fontFamily: "'Sora', sans-serif",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
              >
                {saving
                  ? <><span style={{ width:14, height:14, border:"2px solid #fff", borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.7s linear infinite", display:"inline-block" }} /> Saving…</>
                  : isEditing ? "Update Category" : "Save Category"
                }
              </button>
              <button type="button" onClick={closeDrawer} style={{
                padding: "11px 18px", background: "#f1f5f9", color: "#64748b",
                border: "1.5px solid #e2e8f0", borderRadius: 9,
                fontSize: 13, fontWeight: 700, cursor: "pointer",
                fontFamily: "'Sora', sans-serif",
              }}>
                Cancel
              </button>
            </div>
          </form>
        </DrawerPanel>
      </div>
    </>
  );
}